import type {ComputedRef, Ref} from 'vue';
import type {Object3D} from 'three';
import {
    isSketchGroup,
    SketchDisplayState,
    useDispatchEvent,
    ViewerMode,
    type AssemblyComponentFixationState,
    type MeasureTreeNodeVisualSelectionDescriptor,
    type SelectionInteractionMeta,
} from '@puppycad/cad-core';
import {
    FeatureTreeDatumReferenceKind,
    FeatureTreeNodeKind,
    hasFeatureTreeDatumPlaneId,
    type FeatureTreeAssemblyComponentDeleteActionInput,
    type FeatureTreeDatumReferenceDeleteActionInput,
    type FeatureTreeNodeMeta,
    type FeatureTreeObjectSummary,
    type FeatureTreeProviders,
} from './feature-tree-types';
import {createFeatureRollbackActions} from './create-feature-rollback-actions';
import {resolveFeatureRollbackBarDisabled} from './resolve-feature-rollback-bar-disabled';
import {resolveEditSketchRequest} from './resolve-edit-sketch-request';
import {requestFeatureTreeDeleteConfirmation} from '@/views/editor/requestFeatureTreeDeleteConfirmation';
import {resolveAssemblyMateController} from '@/utils';
import {
    resolveFeatureTreeMeasurableObjectId,
} from './resolve-measure-selection-tree-projection';
import {runTreeToMeasureSelectionSync} from './measure-feature-tree-sync-guard';

//------ 线性零部件阵列节点删除 开始------
/**
 * 线性零部件阵列运行时节点类型。
 */
enum LinearComponentPatternRuntimeNodeType {
    /** 阵列分组节点。 */
    Group = 'LinearPatternRuntimeGroup',
}
//------ 线性零部件阵列节点删除 结束------

/**
 * 设计树选中被吸收草图时使用的静默显示选项。
 *
 * 设计说明：
 * 1. 选中草图引用只是在设计树中提供临时可视反馈，不代表模型生命周期发生变化；
 * 2. 若发布生命周期事件，Editor 会刷新设计树，进而把用户展开的特征节点重置；
 * 3. 统一复用同一个只读选项对象，避免在树选中链路中反复创建临时配置。
 */
const silentSketchDisplayStateOptions = {
    publishLifecycleEvent: false,
} as const;

/**
 * 判断 GLB 节点是否代表可删除的装配组件。
 *
 * @param nodeMeta 设计树节点元数据。
 * @returns 节点代表装配、零件或兜底组件时返回 true。
 */
function isAssemblyComponentDeleteTarget(nodeMeta: FeatureTreeNodeMeta): boolean {
    return nodeMeta.kind === FeatureTreeNodeKind.GlbNode
        && (
            nodeMeta.glbNodeRole === 'assembly'
            || nodeMeta.glbNodeRole === 'part'
            || nodeMeta.glbNodeRole === 'component'
        );
}

/**
 * 创建装配组件删除动作输入。
 *
 * @param treeKey 触发动作的设计树节点 key。
 * @param nodeMeta 设计树节点元数据。
 * @returns 可交给 Editor 装配删除边界的输入；缺少对象 ID 时返回 null。
 */
function createAssemblyComponentDeleteActionInput(
    treeKey: string,
    nodeMeta: FeatureTreeNodeMeta,
): FeatureTreeAssemblyComponentDeleteActionInput | null {
    const objectId = (nodeMeta.objectId ?? nodeMeta.glbSourceObjectId ?? '').trim();
    const sourceObjectId = nodeMeta.glbSourceObjectId?.trim() || undefined;
    const stableNodeKey = ((nodeMeta as { readonly nodeKey?: string }).nodeKey ?? nodeMeta.glbAssemblyNodeId ?? '').trim();

    if (!objectId || !nodeMeta.glbNodeRole) {
        return null;
    }

    return {
        treeKey,
        ...(stableNodeKey ? { nodeKey: stableNodeKey } : {}),
        objectId,
        ...(sourceObjectId ? { sourceObjectId } : {}),
        ...(nodeMeta.displayName ? { displayName: nodeMeta.displayName } : {}),
        nodeRole: nodeMeta.glbNodeRole,
    };
}

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/hooks/feature-tree` 目录；
 * 2. 负责封装设计树节点动作处理逻辑；
 * 3. 通过依赖注入消费 Viewer 门面与 store 状态，避免把动作逻辑继续堆在 Pinia store 文件中。
 *
 * 文件职责：
 * 1. 统一处理基准面、特征、草图、实体节点的动作；
 * 2. 让 `useFeatureTree.ts` 只保留状态组装，不直接承载全部行为细节；
 * 3. 为后续继续扩展节点右键动作与交互策略提供集中入口。
 */

/**
 * 设计树动作依赖。
 */
export interface FeatureTreeActionContext {
    /** 获取 Viewer 运行时。 */
    getViewer: () => any | null;
    /** 节点元数据映射。 */
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>;
    /** 当前选中节点。 */
    selectedKeys: Ref<string[]>;
    /** 当前选中对象。 */
    selectedObject: Ref<any>;
    /** 当前绘图基准面 ID。 */
    currentDrawingDatumPlaneId: Ref<number | null>;
    /** 当前选中基准面 ID。 */
    selectedDatumPlaneId: Ref<number | null>;
    /** 是否处于草图平面选中态。 */
    isSketchPlaneSelected: Ref<boolean>;
    /** 当前 Viewer 模式。 */
    viewerMode: Ref<ViewerMode>;
    /** 是否正在绘图。 */
    isDrawingMode: Ref<boolean>;
    /** 待执行草图命令。 */
    pendingSketchToolCommand: Ref<string | null>;
    /** 是否存在待执行草图命令。 */
    hasPendingSketchCommand: ComputedRef<boolean>;
    /** 读取 NaiveUI 消息实例。 */
    getMessage: () => FeatureTreeProviders['message'] | null;
    getDialog?: () => FeatureTreeProviders['dialog'] | null;
    /** 读取进入草图编辑回调。 */
    getEnterSketchEditingOnDatumPlane: () => FeatureTreeProviders['enterSketchEditingOnDatumPlane'] | null;
    /** 读取执行草图命令回调。 */
    getHandleSketchCommand: () => FeatureTreeProviders['handleSketchCommand'] | null;
    /** 读取编辑特征回调。 */
    getHandleFeatureEdit: () => FeatureTreeProviders['handleFeatureEdit'] | null;
    //------ 基准面编辑特征 开始------
    /** 读取编辑基准面特征回调。 */
    getHandleReferencePlaneEdit?: () => FeatureTreeProviders['handleReferencePlaneEdit'] | null;
    //------ 基准面编辑特征 结束------
    //------ 基准轴编辑特征 开始------
    /** 读取编辑基准轴特征回调。 */
    getHandleReferenceAxisEdit?: () => FeatureTreeProviders['handleReferenceAxisEdit'] | null;
    //------ 基准轴编辑特征 结束------
    //------ 基准点编辑特征 开始------
    /** 读取编辑基准点特征回调。 */
    getHandleReferencePointEdit?: () => FeatureTreeProviders['handleReferencePointEdit'] | null;
    //------ 基准点编辑特征 结束------
    /** 读取编辑草图回调。 */
    getHandleFeatureEditSketch: () => FeatureTreeProviders['handleFeatureEditSketch'] | null;
    /** 读取设计树草图生命周期动作回调。 */
    getHandleSketchLifecycleAction: () => FeatureTreeProviders['handleSketchLifecycleAction'] | null;
    //------ 基准对象删除历史 开始------
    /** 读取设计树基准对象删除动作回调。 */
    getHandleDatumReferenceDeleteAction?: () => FeatureTreeProviders['handleDatumReferenceDeleteAction'] | null;
    //------ 基准对象删除历史 结束------
    /** 读取设计树特征删除动作回调。 */
    getHandleFeatureDeleteAction?: () => FeatureTreeProviders['handleFeatureDeleteAction'] | null;
    /** 读取设计树装配组件删除动作回调。 */
    getHandleAssemblyComponentDeleteAction?:
        () => FeatureTreeProviders['handleAssemblyComponentDeleteAction'] | null;
    /** 读取设计树特征显隐动作回调。 */
    getHandleFeatureVisibilityAction?: () => FeatureTreeProviders['handleFeatureVisibilityAction'] | null;
    /** 读取退回控制棒动作回调。 */
    getHandleFeatureRollbackAction?: () => FeatureTreeProviders['handleFeatureRollbackAction'] | null;
    /** 读取装配体零部件固定/浮动切换回调。 */
    getHandleAssemblyComponentFixationChange:
        () => FeatureTreeProviders['handleAssemblyComponentFixationChange'] | null;
    /** 读取设计树 Mate 编辑回调。 */
    getHandleAssemblyMateEdit?:
        () => FeatureTreeProviders['handleAssemblyMateEdit'] | null;
    //------ 线性零部件阵列节点删除 开始------
    /** 读取线性零部件阵列分组删除回调。 */
    getHandleLinearComponentPatternDelete?:
        () => FeatureTreeProviders['handleLinearComponentPatternDelete'] | null;
    /** 读取线性零部件阵列分组编辑回调。 */
    getHandleLinearComponentPatternEdit?:
        () => FeatureTreeProviders['handleLinearComponentPatternEdit'] | null;
    //------ 线性零部件阵列节点删除 结束------
    /** 清除待执行草图命令。 */
    clearPendingSketchToolCommand: () => void;
    /** 清除选中基准面预览。 */
    clearSelectedDatumPlanePreview: () => void;
    /** 根据 ID 读取场景对象。 */
    getSceneObjectById: (objectId: string) => Object3D | null;
    /** 根据 ID 读取设计树对象摘要。 */
    getObjectById: (objectId: string) => Promise<FeatureTreeObjectSummary | null>;
    /** 刷新设计树；名称变化声明精确范围，其余动作仍请求完整结构投影。 */
    refreshTree: (nameChange?: FeatureTreeNameChange) => Promise<{ objectCount: number; commandCount: number }>;
    /** 对现有节点应用可见性增量；节点不存在时返回 false，由动作层回退完整刷新。 */
    applyNodeVisibilityChange?: (key: string, hidden: boolean) => boolean;
    /** 重命名请求。 */
    requestRename: (title: string, defaultValue: string) => Promise<string | null>;
    /** 对象名称覆写表。 */
    objectNameOverrides: Map<string, string>;
    /** 解析草图实体类型。 */
    resolveSketchEntityTypeByMeshClass: (object: Object3D) => string | null;
}

/**
 * 创建设计树动作处理器。
 * @param context 动作依赖。
 * @returns 节点动作函数集合。
 */
export function createFeatureTreeActions(context: FeatureTreeActionContext) {
    const {
        getViewer,
        featureTreeNodeMap,
        selectedKeys,
        selectedObject,
        currentDrawingDatumPlaneId,
        selectedDatumPlaneId,
        isSketchPlaneSelected,
        viewerMode,
        isDrawingMode,
        pendingSketchToolCommand,
        hasPendingSketchCommand,
        getMessage,
        getDialog = () => null,
        getEnterSketchEditingOnDatumPlane,
        getHandleSketchCommand,
        getHandleFeatureEdit,
        //------ 基准面编辑特征 开始------
        getHandleReferencePlaneEdit = () => null,
        //------ 基准面编辑特征 结束------
        //------ 基准轴编辑特征 开始------
        getHandleReferenceAxisEdit = () => null,
        //------ 基准轴编辑特征 结束------
        //------ 基准点编辑特征 开始------
        getHandleReferencePointEdit = () => null,
        //------ 基准点编辑特征 结束------
        getHandleFeatureEditSketch,
        getHandleSketchLifecycleAction,
        //------ 基准对象删除历史 开始------
        getHandleDatumReferenceDeleteAction = () => null,
        //------ 基准对象删除历史 结束------
        getHandleFeatureDeleteAction = () => null,
        getHandleAssemblyComponentDeleteAction = () => null,
        getHandleFeatureVisibilityAction = () => null,
        getHandleFeatureRollbackAction = () => null,
        getHandleAssemblyComponentFixationChange,
        getHandleAssemblyMateEdit = () => null,
        //------ 线性零部件阵列节点删除 开始------
        getHandleLinearComponentPatternDelete = () => null,
        getHandleLinearComponentPatternEdit = () => null,
        //------ 线性零部件阵列节点删除 结束------
        clearPendingSketchToolCommand,
        clearSelectedDatumPlanePreview,
        getSceneObjectById,
        getObjectById,
        refreshTree,
        applyNodeVisibilityChange = () => false,
        requestRename,
        objectNameOverrides,
        resolveSketchEntityTypeByMeshClass,
    } = context;
    const {
        handleFeatureRollbackToFeature,
        handleFeatureRollbackForward,
        handleFeatureRollbackBarMoveToIndex,
        handleFeatureRollbackBarMoveUp,
        handleFeatureRollbackBarMoveDown,
        handleFeatureRollbackToPrevious,
        handleFeatureRollbackToEnd,
    } = createFeatureRollbackActions({
        getViewer,
        featureTreeNodeMap,
        getHandleFeatureRollbackAction,
        isRollbackBarDisabled: () => resolveFeatureRollbackBarDisabled({
            isDrawingMode: isDrawingMode.value,
            viewerMode: viewerMode.value,
        }),
        getMessage,
        refreshTree,
    });

    /**
     * 判断是否为草图相关模式。
     * @param mode Viewer 模式。
     * @returns 是否为草图相关模式。
     */
    function isSketchViewerMode(mode: ViewerMode): boolean {
        return mode === ViewerMode.Sketch
            || mode === ViewerMode.SketchDrawing
            || mode === ViewerMode.SketchTrim;
    }

    /**
     * 从场景对象构建设计树选择摘要。
     *
     * 说明：设计树状态只需要展示与命令分发所需的轻量字段，不能把 THREE.Object3D
     * 放入 Vue/Pinia 状态，否则大型草图对象会被深度代理，直接影响绘制和 undo/redo 性能。
     *
     * @param sceneObject 场景运行时对象。
     * @param type 设计树对象类型。
     * @returns 轻量选择摘要。
     */
    function createSceneSelectionSummary(sceneObject: Object3D, type: string): FeatureTreeObjectSummary {
        return {
            id: sceneObject.uuid,
            type,
            name: sceneObject.name || type,
            metadata: {
                source: 'scene',
                type,
            },
        };
    }

    //------ 活动草图节点主选隔离 开始------
    /**
     * 判断设计树草图节点是否代表当前正在编辑的草图组。
     *
     * 设计说明：
     * 1. 浏览态选中草图节点时，仍需要同步 Viewer 主选以显示整组缩放框；
     * 2. 进入已有草图编辑后，SolidWorks 不会默认选中草图内全部实体，因此活动草图组不能再被写成主选；
     * 3. 判断只依赖草图节点语义和活动草图 ID，不读取或持有 Three.js 对象到响应式状态。
     *
     * @param input 判断输入。
     * @returns 当前节点代表活动草图组时返回 true。
     */
    function isActiveSketchGroupTreeSelection(input: {
        viewer: any | null;
        nodeMeta: FeatureTreeNodeMeta;
        primarySelectionObjectId: string;
        sceneObject: Object3D | null;
    }): boolean {
        if (input.nodeMeta.objectType !== 'sketch') {
            return false;
        }

        const activeSketchGroupId = input.viewer?.getActiveSketchGroupId?.() ?? null;
        if (!activeSketchGroupId) {
            return false;
        }

        /**
         * 草图组运行时对象 ID 与设计树 objectId 正常一致；sceneObject.uuid 作为兼容兜底，
         * 用于覆盖后续如果出现代理选择对象时的运行时 ID 对齐。
         */
        return activeSketchGroupId === input.primarySelectionObjectId
            || activeSketchGroupId === input.sceneObject?.uuid;
    }
    //------ 活动草图节点主选隔离 结束------

    //------ 特征草图引用节点临时显示 开始------
    /** 当前被特征树选中后临时恢复显示的草图引用 ID。 */
    let temporaryShownSketchRefId: string | null = null;
    /** 当前被临时恢复显示的草图引用原始显示态。 */
    let temporaryShownSketchRefOriginalDisplayState: SketchDisplayState | null = null;

    /**
     * 恢复草图引用节点的临时显示态。
     *
     * 设计说明：
     * 1. 被特征消费的草图默认应保持 Hidden；
     * 2. 若只是为了树选中临时显示，取消选中或切换到其他节点时必须恢复；
     * 3. 位于正式退回或草图严格前缀后缀中的引用节点不可临时显示；
     * 4. `keepSketchId` 用于支持“继续选中同一个草图引用节点”时跳过恢复，避免闪烁。
     *
     * @param keepSketchId 需要保留临时显示的草图 ID。
     */
    function restoreTemporaryShownSketchRef(keepSketchId?: string): void {
        if (!temporaryShownSketchRefId || !temporaryShownSketchRefOriginalDisplayState) {
            return;
        }
        const viewer = getViewer();
        if (viewer?.getActiveSketchGroupId?.() === temporaryShownSketchRefId) {
            /**
             * 草图编辑会话已经接管显示态后，Edit 是 cad-core 的权威状态。
             * 浏览态为选中而保存的 Hidden 只属于进入编辑前的临时显示，不能跨越会话边界写回。
             */
            temporaryShownSketchRefId = null;
            temporaryShownSketchRefOriginalDisplayState = null;
            return;
        }
        if (keepSketchId && temporaryShownSketchRefId === keepSketchId) {
            return;
        }
        viewer?.setSketchGroupDisplayState?.(
            temporaryShownSketchRefId,
            temporaryShownSketchRefOriginalDisplayState,
            silentSketchDisplayStateOptions,
        );
        temporaryShownSketchRefId = null;
        temporaryShownSketchRefOriginalDisplayState = null;
    }

    /**
     * 当选中特征下的草图引用节点时，临时恢复草图显示。
     *
     * @param nodeMeta 当前节点元数据。
     */
    function ensureSketchRefVisibleForSelection(nodeMeta: FeatureTreeNodeMeta | undefined): void {
        if (
            !nodeMeta
            || nodeMeta.kind !== FeatureTreeNodeKind.Object
            || nodeMeta.objectType !== 'sketch'
            || nodeMeta.nodeRole !== 'sketchRef'
            || !nodeMeta.objectId
            || nodeMeta.rolledBack === true
        ) {
            restoreTemporaryShownSketchRef();
            return;
        }

        const sketchId = nodeMeta.objectId;
        const viewer = getViewer();
        if (!viewer?.getSketchGroupDisplayState || !viewer?.setSketchGroupDisplayState) {
            return;
        }

        if (temporaryShownSketchRefId && temporaryShownSketchRefId !== sketchId) {
            restoreTemporaryShownSketchRef();
        }
        if (temporaryShownSketchRefId === sketchId) {
            return;
        }

        const currentDisplayState = viewer.getSketchGroupDisplayState(sketchId);
        if (currentDisplayState !== SketchDisplayState.Hidden) {
            temporaryShownSketchRefId = null;
            temporaryShownSketchRefOriginalDisplayState = null;
            return;
        }

        viewer.setSketchGroupDisplayState(
            sketchId,
            SketchDisplayState.Shown,
            silentSketchDisplayStateOptions,
        );
        temporaryShownSketchRefId = sketchId;
        temporaryShownSketchRefOriginalDisplayState = SketchDisplayState.Hidden;
    }
    //------ 特征草图引用节点临时显示 结束------

    //------ 设计树基准对象草图选中保持面板 开始------
    /**
     * 创建设计树默认选择元信息。
     *
     * 设计说明：
     * 1. 设计树基准点、基准轴、基准面与草图点击只负责树选中和视口高亮，不应强制打开属性页；
     * 2. 这里仍复用 Viewer 程序化选择链路，避免破坏现有高亮与选择快照；
     * 3. 通过 `feature-tree/default` 让右侧面板订阅层精确识别并保持当前 tab。
     *
     * @param displayName 选择目标显示名。
     * @returns 程序化选择元信息。
     */
    function createFeatureTreeDefaultSelectionMeta(displayName?: string): SelectionInteractionMeta {
        return {
            source: 'feature-tree' as const,
            semantic: 'default' as const,
            displayName,
        };
    }
    //------ 设计树基准对象草图选中保持面板 结束------

    /**
     * 设计树选中基准面时仅同步选中态与视口高亮。
     *
     * 设计说明：
     * 1. SolidWorks 中设计树点击基准面只会选中，不会进入草图编辑或切换正视相机；
     * 2. 进草图、正视于等动作必须走独立入口（右键菜单、草图 armed 态、视图命令）；
     * 3. 行为与基准轴/基准点选中保持一致，避免误写 `currentDrawingDatumPlaneId`。
     *
     * @param planeId 基准面 ID。
     */
    function handleDatumPlaneTreeSelect(planeId: number): void {
        const viewer = getViewer();
        if (!viewer) {
            return;
        }

        viewer.showDatumPlaneTemporarily?.(planeId);
        selectedDatumPlaneId.value = planeId;

        const planeSummary = viewer.getDatumPlaneSummary?.(planeId) ?? null;
        selectedObject.value = planeSummary
            ? {
                id: planeSummary.id,
                type: 'datum-plane',
                name: planeSummary.name,
                metadata: {
                    type: 'Datum Plane',
                },
            }
            : null;

        const datumPlaneObjectId = viewer.getDatumPlane?.(planeId)?.getObject3D?.().uuid ?? null;
        if (datumPlaneObjectId) {
            viewer.selectEntityByObjectId?.(datumPlaneObjectId, {
                interactionMeta: createFeatureTreeDefaultSelectionMeta(planeSummary?.name),
            });
        }
    }

    /**
     * 处理基准面点击。
     * @param planeId 基准面 ID。
     */
    function handleDatumPlaneClick(planeId: number): void {
        const viewer = getViewer();
        const message = getMessage();
        if (isSketchViewerMode(viewerMode.value)) {
            viewer?.showDatumPlaneTemporarily(planeId);
            selectedDatumPlaneId.value = planeId;
            if (planeId !== currentDrawingDatumPlaneId.value) {
                message?.warning('请先退出当前草图');
            }
            return;
        }
        if (hasPendingSketchCommand.value) {
            selectedDatumPlaneId.value = planeId;
            handleEnterDatumPlaneSketch(String(planeId));
            return;
        }
        handleDatumPlaneTreeSelect(planeId);
    }

    /**
     * 处理基准面悬停。
     * @param planeId 基准面 ID。
     * @param hovered 是否处于悬停状态。
     */
    function handleDatumPlaneHover(planeId: string, hovered: boolean): void {
        const numericPlaneId = Number(planeId);
        if (isSketchViewerMode(viewerMode.value) && numericPlaneId === currentDrawingDatumPlaneId.value) {
            return;
        }
        if (isDrawingMode.value && numericPlaneId === currentDrawingDatumPlaneId.value) {
            return;
        }
        const viewer = getViewer();
        if (!viewer) {
            return;
        }
        if (hovered) {
            const datumPlaneObjectId = viewer.getDatumPlane?.(numericPlaneId)?.getObject3D?.().uuid ?? null;
            viewer.setProgrammaticHover?.(datumPlaneObjectId);
            viewer.showDatumPlaneTemporarily(numericPlaneId);
            return;
        }
        viewer.setProgrammaticHover?.(null);
        //------ 设计树默认基准面移出隐藏 开始------
        /**
         * 设计树选中态只负责保留树节点选中和实体选择快照，不应继续持有动态高亮的临时显示。
         * 当前草图绘制基准面仍由草图会话生命周期持有，因此移出设计树时不能隐藏它。
         */
        if (numericPlaneId === currentDrawingDatumPlaneId.value) {
            return;
        }
        //------ 设计树默认基准面移出隐藏 结束------
        viewer.hideTemporaryDatumPlane(numericPlaneId);
    }

    /**
     * 解析正式基准对象对应的场景对象 ID。
     *
     * 设计说明：
     * 1. 设计树 hover 不走真实射线拾取，因此必须主动把 datum 节点还原成场景 Object3D；
     * 2. 基准面使用 group 根对象，基准轴与基准点使用自身对象，统一返回 `uuid` 给程序化 hover；
     * 3. 只在动作层收口这段解析，避免 FeatureTree 组件直接依赖 cad-core 运行时细节。
     *
     * @param nodeMeta 基准节点元数据。
     * @returns 对应场景对象 ID；无法解析时返回 `null`。
     */
    function resolveDatumHoverObjectId(nodeMeta: FeatureTreeNodeMeta | undefined): string | null {
        const viewer = getViewer();
        if (!viewer || !nodeMeta) {
            return null;
        }

        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            return viewer.getDatumPlane?.(nodeMeta.planeId)?.getObject3D?.().uuid ?? null;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            return viewer.getDatumAxis?.(nodeMeta.axisId)?.getObject3D?.().uuid ?? null;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            return viewer.getDatumPoint?.(nodeMeta.pointId)?.getObject3D?.().uuid ?? null;
        }

        return null;
    }

    /**
     * 处理设计树中的正式基准对象移入态。
     *
     * 设计说明：
     * 1. 基准面除了程序化 hover，还要保留原有的临时显示逻辑；
     * 2. 基准轴与基准点没有单独的树 hover 收口点，这里统一补齐；
     * 3. 所有 datum 节点离开设计树时，都通过同一入口清理 3D hover，避免遗留高亮。
     *
     * @param key 设计树节点 key。
     * @param hovered 是否处于移入状态。
     */
    function handleDatumReferenceHover(key: string, hovered: boolean): void {
        const nodeMeta = featureTreeNodeMap.get(key);
        if (!nodeMeta) {
            return;
        }

        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            handleDatumPlaneHover(String(nodeMeta.planeId), hovered);
            return;
        }

        if (
            nodeMeta.kind !== FeatureTreeNodeKind.DatumAxis
            && nodeMeta.kind !== FeatureTreeNodeKind.DatumPoint
        ) {
            return;
        }

        const viewer = getViewer();
        if (!viewer) {
            return;
        }

        const hoverObjectId = hovered ? resolveDatumHoverObjectId(nodeMeta) : null;
        viewer.setProgrammaticHover?.(hoverObjectId);
    }

    /**
     * 处理特征节点悬停。
     */
    function buildGlbInteractionMeta(nodeMeta: FeatureTreeNodeMeta | undefined): SelectionInteractionMeta | null {
        if (!nodeMeta || nodeMeta.kind !== FeatureTreeNodeKind.GlbNode) {
            return null;
        }
        return {
            source: 'feature-tree',
            semantic: 'entity',
            assemblyNodeId: nodeMeta.assemblyNodeId,
            projectNodeKey: nodeMeta.glbAssemblyNodeId,
            glbNodeRole: nodeMeta.glbNodeRole,
            bodyBindingId: nodeMeta.bodyBindingId,
            displayName: nodeMeta.displayName,
            entityHighlightTargets: nodeMeta.highlightTargets,
        };
    }

    /**
     * 创建设计树程序化选择元信息。
     *
     * @param nodeMeta 设计树节点元数据。
     * @returns 可传给 cad-core 选择链路的交互元信息。
     */
    function buildFeatureTreeSelectionInteractionMeta(
        nodeMeta: FeatureTreeNodeMeta | undefined,
    ): SelectionInteractionMeta | null {
        const glbInteractionMeta = buildGlbInteractionMeta(nodeMeta);
        if (glbInteractionMeta) {
            return glbInteractionMeta;
        }
        if (!nodeMeta || nodeMeta.kind !== FeatureTreeNodeKind.Object || !nodeMeta.assemblyNodeId) {
            return null;
        }

        return {
            source: 'feature-tree',
            semantic: 'entity',
            assemblyNodeId: nodeMeta.assemblyNodeId,
            projectNodeKey: 'nodeKey' in nodeMeta && typeof nodeMeta.nodeKey === 'string'
                ? nodeMeta.nodeKey
                : undefined,
            displayName: nodeMeta.displayName,
        };
    }

    /**
     * 解析设计树 hover 对应的真实场景对象锚点。
     *
     * 设计树组件只能稳定传递节点 key，`.puppy` 实体节点的 `refObjectId`
     * 可能为空；这时必须回到 GLB/.puppy 统一节点元数据中读取对象 ID，
     * 避免把导入来源差异泄漏到视口 hover 链路。
     *
     * @param payloadRefObjectId 组件层透传的对象 ID。
     * @param nodeMeta 设计树节点元数据。
     * @returns 可用于 cad-core 程序化 hover 的场景对象 ID，缺失时返回空字符串。
     */
    function resolveFeatureTreeHoverObjectId(
        payloadRefObjectId: string | undefined,
        nodeMeta: FeatureTreeNodeMeta | undefined,
    ): string {
        const payloadObjectId = payloadRefObjectId?.trim() ?? '';
        if (payloadObjectId.length > 0) {
            return payloadObjectId;
        }
        if (nodeMeta?.kind !== FeatureTreeNodeKind.GlbNode) {
            return '';
        }
        return nodeMeta.objectId?.trim() ?? '';
    }

    /**
     * 同步 imported-model 树节点选中摘要。
     *
     * 设计说明：
     * 1. `.puppy` / GLB 导入体不属于 Worker 原生几何摘要，不能继续用 `getObjectById` 回填属性面板；
     * 2. cad-core 已经通过 `buildImportedSelectionSummary` 统一封装 selection proxy、显示名与 body 语义；
     * 3. 树到视口的程序化选择不应依赖 EntitySelected 再回流一次 UI，否则重复选中同一对象时会因为 selectedPicks 未变化而拿不到回流事件。
     *
     * @param objectId 当前树节点映射到的场景对象 ID。
     * @param interactionMeta GLB/imported 选择语义。
     * @returns 是否成功写入 imported-model 选中摘要。
     */
    function syncImportedTreeSelectionSummary(
        objectId: string,
        interactionMeta: ReturnType<typeof buildGlbInteractionMeta>,
    ): boolean {
        if (!interactionMeta) {
            return false;
        }
        const viewer = getViewer();
        const summary = viewer?.buildImportedSelectionSummary?.(objectId, {
            interactionMeta,
        });
        if (!summary?.selectedObject) {
            return false;
        }
        selectedObject.value = summary.selectedObject;
        return true;
    }

    /**
     * 提取 GLB datum-slot 描述符。
     *
     * 设计说明：
     * 1. datum-slot 不是正式可选实体，不能走现有 `selectEntityByObjectId` 链路；
     * 2. 这里统一把树节点元数据还原成 cad-core 可识别的 descriptor，
     *    供 hover / click / clear 三条动作链复用。
     *
     * @param nodeMeta 节点元数据。
     * @returns datum-slot 描述符；当前节点不是 datum-slot 时返回 `null`。
     */
    function buildGlbDatumSlotDescriptor(nodeMeta: FeatureTreeNodeMeta | undefined) {
        if (nodeMeta?.kind !== FeatureTreeNodeKind.GlbDatumSlot || !nodeMeta.glbDatumSlot) {
            return null;
        }
        return nodeMeta.glbDatumSlot;
    }

    /**
     * 读取装配体 Mate 控制器。
     *
     * @returns cad-core Mate 控制器；未挂载时返回 `null`。
     */
    function getAssemblyMateController() {
        const viewer = getViewer();
        return resolveAssemblyMateController(viewer);
    }

    /**
     * 清理设计树 Mate 节点选中高亮。
     *
     * 设计说明：
     * Mate 节点选中使用 cad-core 根据 mateId 恢复参与实体并高亮。普通设计树节点重新选中后，
     * 必须显式清掉该通道，否则 Mate 几何会继续保持蓝色高亮，表现为“退出 Mate 节点后仍选中”。
     */
    function clearAssemblyMateTreeSelectionHighlight(): void {
        getAssemblyMateController()?.selectMate?.(null);
    }

    /**
     * 判断节点是否为 Mate 关系节点。
     *
     * @param nodeMeta 节点元数据。
     * @returns 是否为 Mate 关系节点。
     */
    function isAssemblyMateTreeNode(nodeMeta: FeatureTreeNodeMeta | undefined): boolean {
        return nodeMeta?.kind === FeatureTreeNodeKind.AssemblyMate;
    }

    /**
     * 解析 Mate 节点的稳定 ID。
     *
     * @param nodeMeta 节点元数据。
     * @returns Mate ID；缺失时返回 `null`。
     */
    function resolveAssemblyMateId(nodeMeta: FeatureTreeNodeMeta | undefined): string | null {
        const mateId = typeof nodeMeta?.mateId === 'string' ? nodeMeta.mateId.trim() : '';
        return mateId.length > 0 ? mateId : null;
    }

    /**
     * 解析当前设计树已选中的 Mate ID。
     *
     * 设计说明：
     * Mate 节点 hover 使用的是 cad-core 的命令态高亮通道。鼠标移出临时 hover 目标时，必须恢复
     * 设计树当前选中 Mate，而不是无条件清空高亮，否则会把用户明确选中的配合关系视觉反馈抹掉。
     *
     * @returns 当前已选 Mate ID；当前树选中不是 Mate 节点时返回 null。
     */
    function resolveSelectedAssemblyMateId(): string | null {
        const selectedKey = selectedKeys.value[0] ?? '';
        if (!selectedKey) {
            return null;
        }

        const selectedNodeMeta = featureTreeNodeMap.get(selectedKey);
        if (!isAssemblyMateTreeNode(selectedNodeMeta)) {
            return null;
        }

        return resolveAssemblyMateId(selectedNodeMeta);
    }

    /**
     * 同步 Mate 节点 hover 高亮。
     *
     * @param controller cad-core Mate 控制器。
     * @param mateId 当前 hover 的 Mate ID。
     * @param hovered 是否进入 hover。
     */
    function syncAssemblyMateTreeHoverHighlight(
        controller: {
            selectMate?: (mateId: string | null) => void;
            selectMates?: (mateIds: readonly string[]) => void;
        },
        mateId: string,
        hovered: boolean,
    ): void {
        const selectedMateId = resolveSelectedAssemblyMateId();
        if (hovered) {
            if (selectedMateId && selectedMateId !== mateId && controller.selectMates) {
                controller.selectMates([selectedMateId, mateId]);
                return;
            }
            controller.selectMate?.(mateId);
            return;
        }

        controller.selectMate?.(selectedMateId);
    }

    /**
     * 解析 Mate 删除确认中使用的显示名称。
     *
     * @param nodeMeta 设计树节点元数据。
     * @param mateId cad-core Mate ID。
     * @returns 用于确认弹窗的 Mate 名称。
     */
    function resolveAssemblyMateDeleteLabel(
        nodeMeta: FeatureTreeNodeMeta,
        mateId: string,
    ): string {
        const displayName = typeof nodeMeta.displayName === 'string'
            ? nodeMeta.displayName.trim()
            : '';
        return displayName.length > 0 ? displayName : mateId;
    }

    function handleFeatureNodeHover(payload: {
        key: string;
        refObjectId?: string;
        hovered: boolean;
    }): void {
        const viewer = getViewer();
        const nodeMeta = featureTreeNodeMap.get(payload.key);
        const datumSlotDescriptor = buildGlbDatumSlotDescriptor(nodeMeta);
        if (datumSlotDescriptor) {
            viewer?.setImportedModelDatumSlotHover?.(
                payload.hovered ? datumSlotDescriptor : null,
            );
            return;
        }
        const refObjectId = payload.refObjectId?.trim() ?? '';
        if (nodeMeta?.objectType === 'feature') {
            const featureId = (nodeMeta.objectId || payload.key).trim();
            const resultObjectIds = Array.isArray(nodeMeta.resultObjectIds)
                ? nodeMeta.resultObjectIds.filter((resultObjectId) => resultObjectId.trim().length > 0)
                : (refObjectId ? [refObjectId] : []);
            viewer?.setFeatureVisualHoverScope?.(
                payload.hovered ? featureId : null,
                payload.hovered ? resultObjectIds : [],
            );
            return;
        }
        const hoverObjectId = resolveFeatureTreeHoverObjectId(refObjectId, nodeMeta);
        if (!hoverObjectId) {
            return;
        }
        const interactionMeta = buildFeatureTreeSelectionInteractionMeta(nodeMeta);
        if (interactionMeta) {
            viewer?.setProgrammaticHover?.(
                payload.hovered ? hoverObjectId : null,
                {interactionMeta},
            );
            return;
        }
        viewer?.setProgrammaticHover?.(payload.hovered ? hoverObjectId : null);
    }

    /**
     * 处理 Mate 设计树节点悬停。
     *
     * 设计说明：
     * 1. Mate 节点本身没有场景 Object3D，不能走普通 `refObjectId` hover；
     * 2. cad-core 控制器已知道 Mate Definition 与稳定拓扑引用，因此 hover 只传 `mateId`；
     * 3. 已选中 Mate 时再 hover 其它 Mate，需要临时同时保留 selected 与 hover 高亮；
     * 4. 移出 hover Mate 后恢复当前已选中 Mate，不能把 selected Mate 高亮清空。
     *
     * @param key 设计树节点 key。
     * @param hovered 是否悬停。
     */
    function handleAssemblyMateNodeHover(key: string, hovered: boolean): void {
        const nodeMeta = featureTreeNodeMap.get(key);
        if (!isAssemblyMateTreeNode(nodeMeta)) {
            return;
        }

        const mateId = resolveAssemblyMateId(nodeMeta);
        const controller = getAssemblyMateController();
        if (!mateId || !controller?.selectMate) {
            return;
        }

        syncAssemblyMateTreeHoverHighlight(controller, mateId, hovered);
    }

    //------ 线性零部件阵列节点多目标交互 开始------
    /**
     * 处理设计树中的多目标悬停。
     *
     * 设计说明：
     * 1. 线性零部件阵列运行时分组节点本身不是可拾取实体，必须转发为零部件对象列表；
     * 2. 统一复用 viewer 的多目标程序化悬停入口，避免组件层直接依赖 cad-core 交互细节；
     * 3. 移出时传空数组清理当前多目标悬停，防止残留包围盒高亮。
     *
     * @param payload 多目标悬停载荷。
     */
    function handleFeatureNodeHoverTargets(payload: {
        refObjectIds: readonly string[];
        hovered: boolean;
    }): void {
        const viewer = getViewer();
        const nextObjectIds = payload.hovered ? payload.refObjectIds : [];
        viewer?.setProgrammaticHoverTargets?.(
            nextObjectIds,
            {
                interactionMeta: {
                    source: 'feature-tree',
                    semantic: 'entity',
                    glbNodeRole: 'group',
                },
            },
        );
    }

    /**
     * 处理设计树中的多目标选中。
     *
     * 设计说明：
     * 1. 线性零部件阵列分组节点在树上保留单节点选中态，但视口主选应是零部件对象集合；
     * 2. 这里统一清理与多目标选择互斥的 datum-slot、草图缩放与临时草图显示状态；
     * 3. 真实多目标主选仍由 cad-core viewer 接管，保持选择链路一致。
     *
     * @param payload 多目标选中载荷。
     */
    function handleFeatureNodeSelectTargets(payload: {
        key: string;
        refObjectIds: readonly string[];
        //------ 线性阵列子零件节点选中高亮 开始------
        /**
         * 当前选择目标的 GLB 节点语义角色。
         *
         * 设计说明：
         * 线性阵列分组点击仍按 `group` 处理；分组下单个复制体点击必须保留 `part`
         * 语义，避免 cad-core 的导入模型高亮控制器把单零件误判为分组。
         */
        glbNodeRole?: SelectionInteractionMeta['glbNodeRole'];
        //------ 线性阵列子零件节点选中高亮 结束------
        //------ 线性阵列来源对象粉色高亮 开始------
        /**
         * 线性阵列源对象 ID 列表，仅用于 cad-core 视觉层显示来源高亮。
         */
        sourceObjectIds?: readonly string[];
        //------ 线性阵列来源对象粉色高亮 结束------
    }): void {
        const viewer = getViewer();
        const previousKey = selectedKeys.value[0] ?? '';
        const previousNodeMeta = previousKey ? featureTreeNodeMap.get(previousKey) : undefined;
        const previousDatumSlotDescriptor = buildGlbDatumSlotDescriptor(previousNodeMeta);
        //------ 线性阵列来源对象粉色高亮 开始------
        const sourceObjectIds = Array.from(new Set(
            (payload.sourceObjectIds ?? [])
                .map((sourceObjectId) => sourceObjectId.trim())
                .filter((sourceObjectId) => sourceObjectId.length > 0),
        ));
        //------ 线性阵列来源对象粉色高亮 结束------
        //------ 线性阵列子零件节点选中高亮 开始------
        const glbNodeRole = payload.glbNodeRole ?? 'group';
        //------ 线性阵列子零件节点选中高亮 结束------

        restoreTemporaryShownSketchRef();
        clearSelectedDatumPlanePreview();
        viewer?.stopSketchScaleControl?.();
        if (previousDatumSlotDescriptor) {
            viewer?.clearImportedModelDatumSlotHighlight?.();
        }

        selectedKeys.value = [payload.key];
        selectedObject.value = null;
        viewer?.selectEntitiesByObjectIds?.(
            payload.refObjectIds,
            {
                interactionMeta: {
                    source: 'feature-tree',
                    semantic: 'entity',
                    glbNodeRole,
                    //------ 线性阵列来源对象粉色高亮 开始------
                    linearComponentPatternSourceObjectIds: sourceObjectIds,
                    //------ 线性阵列来源对象粉色高亮 结束------
                },
            },
        );
    }
    //------ 线性零部件阵列节点多目标交互 结束------

    /**
     * 从设计树进入基准面草图编辑。
     * @param planeId 基准面 ID。
     */
    function handleEnterDatumPlaneSketch(planeId: string): void {
        const viewer = getViewer();
        const message = getMessage();
        const enterSketchEditingOnDatumPlane = getEnterSketchEditingOnDatumPlane();
        const handleSketchCommand = getHandleSketchCommand();
        if (!viewer) {
            return;
        }
        if (isSketchViewerMode(viewerMode.value)) {
            message?.warning('请先退出当前草图');
            return;
        }
        //------ 用户创建基准面进入草图 开始------
        const plane = viewer.getDatumPlaneSummary(Number(planeId));
        if (!plane) {
            message?.warning('未找到基准面，无法进入草图');
            return;
        }
        //------ 用户创建基准面进入草图 结束------
        void (async () => {
            await enterSketchEditingOnDatumPlane?.(Number(planeId), plane.name);
            const pendingCommand = pendingSketchToolCommand.value;
            if (pendingCommand && pendingCommand !== 'sketch-enter') {
                clearPendingSketchToolCommand();
                await handleSketchCommand?.(pendingCommand);
            }
        })();
    }

    //------ 设计树正视于 开始------
    /**
     * 从设计树节点解析可正视的基准面 ID。
     *
     * 设计说明：
     * 1. 基准面节点直接携带 `planeId`，可直接进入基准面正视；
     * 2. 草图节点自身不应重复保存平面姿态，必须通过 cad-core 的草图组关联基准面读取；
     * 3. 该方法只返回稳定 ID，不持有 `ReferencePlane` 或 `SketchGroup` 运行时对象，避免 Vue 响应式污染 Three.js 对象。
     *
     * @param key 设计树节点 key。
     * @returns 可正视基准面 ID；无法解析时返回 null。
     */
    function resolveNormalToDatumPlaneIdFromTreeNode(key: string): number | null {
        const nodeMeta = featureTreeNodeMap.get(key);
        const viewer = getViewer();
        if (!nodeMeta || !viewer) {
            return null;
        }

        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            return nodeMeta.planeId;
        }

        if (nodeMeta.kind === FeatureTreeNodeKind.Object && nodeMeta.objectType === 'sketch' && nodeMeta.objectId) {
            return viewer.getSketchGroupReferencePlaneId?.(nodeMeta.objectId) ?? null;
        }

        return null;
    }

    /**
     * 正视于设计树中的基准面或草图所属基准面。
     *
     * 设计说明：
     * 1. 正视于只改变相机视图，属于 View 行为，不创建草图会话、不写模型历史；
     * 2. 基准面和草图共用 `viewer.enterDatumPlaneView`，确保与进入草图后的正视角一致；
     * 3. 失败时只给出提示，不改写设计树选中态，避免视图命令影响模型选择。
     *
     * @param key 设计树节点 key。
     */
    function handleFeatureTreeNormalTo(key: string): void {
        const viewer = getViewer();
        const message = getMessage();
        const planeId = resolveNormalToDatumPlaneIdFromTreeNode(key);
        if (!viewer || typeof planeId !== 'number') {
            message?.warning('当前节点不支持正视于');
            return;
        }

        const plane = viewer.getDatumPlaneSummary?.(planeId) ?? null;
        if (!plane) {
            message?.warning('未找到可正视的基准面');
            return;
        }

        viewer.enterDatumPlaneView(planeId);
        // 相机状态已由 Viewer 设置，动画与最终画面统一交给宿主帧推进。
        viewer.requestRender();
    }
    //------ 设计树正视于 结束------

    /**
     * 判断测量会话是否激活。
     *
     * @returns 是否处于测量模式。
     */
    function isMeasureSessionActive(): boolean {
        return Boolean(getViewer()?.measureController?.isMeasureActive?.());
    }

    /**
     * 判断节点是否应走测量模式轻量节点选择。
     *
     * @param nodeMeta 节点元数据。
     * @returns 是否使用轻量节点选择。
     */
    function shouldUseMeasureTreeNodeVisualSelection(nodeMeta: FeatureTreeNodeMeta): boolean {
        if (nodeMeta.objectType === 'body') {
            return Boolean(nodeMeta.objectId?.trim());
        }

        if (nodeMeta.kind !== FeatureTreeNodeKind.GlbNode || !nodeMeta.objectId?.trim()) {
            return false;
        }

        return nodeMeta.glbNodeRole === 'assembly'
            || nodeMeta.glbNodeRole === 'part'
            || nodeMeta.glbNodeRole === 'component'
            || nodeMeta.glbNodeRole === 'body';
    }

    /**
     * 构建设计树节点轻量测量选择描述符。
     *
     * @param nodeMeta 节点元数据。
     * @returns 轻量选择描述符。
     */
    function buildMeasureTreeNodeVisualSelectionDescriptor(
        nodeMeta: FeatureTreeNodeMeta,
    ): MeasureTreeNodeVisualSelectionDescriptor | null {
        const objectId = nodeMeta.objectId?.trim();
        if (!objectId) {
            return null;
        }

        return {
            objectId,
            displayName: nodeMeta.displayName,
            glbNodeRole: nodeMeta.glbNodeRole,
            highlightTargets: nodeMeta.highlightTargets,
        };
    }

    /**
     * 测量模式下同步设计树节点轻量高亮与原点浮窗。
     *
     * @param nodeMeta 节点元数据。
     * @returns 是否成功同步。
     */
    function syncTreeNodeVisualToMeasureSelection(
        nodeMeta: FeatureTreeNodeMeta,
    ): boolean {
        const viewer = getViewer();
        const descriptor = buildMeasureTreeNodeVisualSelectionDescriptor(nodeMeta);
        if (!viewer || !descriptor) {
            return false;
        }

        let accepted = false;
        runTreeToMeasureSelectionSync(() => {
            accepted = viewer.applyMeasureTreeNodeVisualSelection?.(descriptor) ?? false;
        });
        return accepted;
    }

    /**
     * 测量模式下同步可测量 objectId 到测量选中列表。
     *
     * @param objectId 场景 objectId。
     * @param interactionMeta 设计树交互元信息。
     */
    function syncMeasurableObjectToMeasureSelection(
        objectId: string,
        interactionMeta?: SelectionInteractionMeta,
    ): void {
        const viewer = getViewer();
        if (!viewer) {
            return;
        }

        runTreeToMeasureSelectionSync(() => {
            viewer.selectEntityByObjectId?.(objectId, {
                interactionMeta: interactionMeta as never,
            });
        });
    }

    /**
     * 测量模式下处理设计树选中。
     *
     * 设计说明：
     * 1. 仅更新 UI 选中态与测量 SSOT，不触发进草图/基准面视图等命令副作用；
     * 2. 不可测量节点给出提示并保持树选中，便于用户识别当前操作对象。
     *
     * @param keys 选中节点键列表。
     */
    async function handleFeatureSelectDuringMeasure(keys: string[]): Promise<void> {
        const viewer = getViewer();
        const message = getMessage();
        const key = keys[0];

        if (!key) {
            selectedKeys.value = [];
            selectedObject.value = null;
            viewer?.clearSelectionProgrammatically?.();
            return;
        }

        const nodeMeta = featureTreeNodeMap.get(key);
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group) {
            selectedKeys.value = [];
            selectedObject.value = null;
            viewer?.clearSelectionProgrammatically?.();
            return;
        }

        if (buildGlbDatumSlotDescriptor(nodeMeta)) {
            selectedKeys.value = keys;
            selectedObject.value = {
                id: key,
                type: 'glb-datum-slot',
                name: nodeMeta.displayName ?? nodeMeta.glbDatumSlot?.slotKind ?? '基准',
                metadata: {
                    type: 'Datum',
                    slotKind: nodeMeta.glbDatumSlot?.slotKind,
                },
            };

            const datumSlotDescriptor = buildGlbDatumSlotDescriptor(nodeMeta);
            if (!datumSlotDescriptor) {
                message?.warning('当前 datum-slot 节点无法解析');
                return;
            }

            let accepted = false;
            runTreeToMeasureSelectionSync(() => {
                accepted = viewer?.applyMeasureUnboundedGlbDatumSlotSelection?.(datumSlotDescriptor) ?? false;
            });
            if (!accepted) {
                message?.warning('当前 datum-slot 节点不支持测量');
            }
            return;
        }

        selectedKeys.value = keys;

        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            selectedDatumPlaneId.value = null;
            const planeSummary = viewer?.getDatumPlaneSummary?.(nodeMeta.planeId) ?? null;
            selectedObject.value = planeSummary
                ? {
                    id: planeSummary.id,
                    type: 'datum-plane',
                    name: planeSummary.name,
                    metadata: { type: 'Datum Plane' },
                }
                : null;
            const objectId = resolveFeatureTreeMeasurableObjectId(nodeMeta, viewer ?? {});
            if (objectId) {
                syncMeasurableObjectToMeasureSelection(objectId);
            }
            return;
        }

        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            const axis = viewer?.getDatumAxis?.(nodeMeta.axisId) ?? null;
            selectedObject.value = axis
                ? {
                    id: axis.id,
                    type: 'datum-axis',
                    name: axis.name,
                    metadata: { type: 'Datum Axis' },
                }
                : null;
            const objectId = resolveFeatureTreeMeasurableObjectId(nodeMeta, viewer ?? {});
            if (objectId) {
                syncMeasurableObjectToMeasureSelection(objectId);
            }
            return;
        }

        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            const pointSummary = viewer?.getDatumPointSummary?.(nodeMeta.pointId) ?? null;
            selectedObject.value = pointSummary
                ? {
                    id: pointSummary.id,
                    type: 'datum-point',
                    name: pointSummary.name,
                    metadata: { type: 'Datum Point' },
                }
                : null;
            const objectId = resolveFeatureTreeMeasurableObjectId(nodeMeta, viewer ?? {});
            if (objectId) {
                syncMeasurableObjectToMeasureSelection(objectId);
            }
            return;
        }

        const objectId = resolveFeatureTreeMeasurableObjectId(nodeMeta, viewer ?? {});
        const interactionMeta = buildFeatureTreeSelectionInteractionMeta(nodeMeta);
        if (!objectId) {
            message?.warning('当前设计树节点无法映射到可测量几何体');
            return;
        }

        if (nodeMeta.objectType === 'sketch' || nodeMeta.objectType === 'feature') {
            message?.warning('请选择面、边、点或基准几何进行测量');
            return;
        }

        if (shouldUseMeasureTreeNodeVisualSelection(nodeMeta)) {
            if (!syncTreeNodeVisualToMeasureSelection(nodeMeta)) {
                message?.warning('当前设计树节点无法解析原点或高亮对象');
            }

            if (nodeMeta.objectType === 'body') {
                selectedObject.value = {
                    id: objectId,
                    type: 'body',
                    name: nodeMeta.displayName ?? '实体',
                    metadata: {
                        type: 'Solid Body',
                        createdByFeatureId: nodeMeta.createdByFeatureId,
                    },
                };
                return;
            }

            selectedObject.value = await getObjectById(objectId);
            return;
        }

        if (interactionMeta) {
            syncMeasurableObjectToMeasureSelection(objectId, interactionMeta);
        } else {
            syncMeasurableObjectToMeasureSelection(objectId);
        }

        if (nodeMeta.objectType === 'body') {
            selectedObject.value = {
                id: objectId,
                type: 'body',
                name: nodeMeta.displayName ?? '实体',
                metadata: {
                    type: 'Solid Body',
                    createdByFeatureId: nodeMeta.createdByFeatureId,
                },
            };
            return;
        }

        selectedObject.value = await getObjectById(objectId);
    }

    /**
     * 处理设计树选中。
     * @param keys 选中节点键列表。
     */
    async function handleFeatureSelect(keys: string[]): Promise<void> {
        if (isMeasureSessionActive()) {
            await handleFeatureSelectDuringMeasure(keys);
            return;
        }

        const viewer = getViewer();
        const previousKey = selectedKeys.value[0] ?? '';
        const previousNodeMeta = previousKey ? featureTreeNodeMap.get(previousKey) : undefined;
        const previousDatumSlotDescriptor = buildGlbDatumSlotDescriptor(previousNodeMeta);
        const key = keys[0];
        if (!key) {
            restoreTemporaryShownSketchRef();
            selectedKeys.value = [];
            selectedObject.value = null;
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            viewer?.setFeatureVisualSelectionScope?.(null);
            if (previousDatumSlotDescriptor) {
                viewer?.clearImportedModelDatumSlotHighlight?.();
                return;
            }
            /**
             * 设计树主动取消选中时，同步清空场景主选，
             * 避免普通实体、原点或基准点在视口中残留选中态。
             */
            viewer?.clearSelectionProgrammatically?.();
            return;
        }
        const nodeMeta = featureTreeNodeMap.get(key);
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group) {
            clearAssemblyMateTreeSelectionHighlight();
            restoreTemporaryShownSketchRef();
            selectedKeys.value = [];
            selectedObject.value = null;
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            viewer?.setFeatureVisualSelectionScope?.(null);
            if (previousDatumSlotDescriptor) {
                viewer?.clearImportedModelDatumSlotHighlight?.();
                return;
            }
            viewer?.clearSelectionProgrammatically?.();
            return;
        }
        const datumSlotDescriptor = buildGlbDatumSlotDescriptor(nodeMeta);
        if (datumSlotDescriptor) {
            clearAssemblyMateTreeSelectionHighlight();
            restoreTemporaryShownSketchRef();
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            viewer?.setFeatureVisualSelectionScope?.(null);
            selectedObject.value = null;
            /**
             * datum-slot 是 info-only overlay，不应残留普通实体主选。
             * 先清掉视口实体选中，再写入树选中态，避免 SelectionCleared 反向把当前树选中抹掉。
             */
            if (!previousDatumSlotDescriptor) {
                viewer?.clearSelectionProgrammatically?.();
            }
            selectedKeys.value = [key];
            viewer?.toggleImportedModelDatumSlotSelection?.(datumSlotDescriptor);
            return;
        }
        if (isAssemblyMateTreeNode(nodeMeta)) {
            const mateId = resolveAssemblyMateId(nodeMeta);
            const controller = getAssemblyMateController();
            restoreTemporaryShownSketchRef();
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            viewer?.setFeatureVisualSelectionScope?.(null);
            if (previousDatumSlotDescriptor) {
                viewer?.clearImportedModelDatumSlotHighlight?.();
            }
            selectedObject.value = null;
            if (!mateId) {
                selectedKeys.value = [];
                getMessage()?.warning('Mate 节点缺少可选中的配合 ID');
                return;
            }
            if (!controller?.selectMate) {
                selectedKeys.value = [];
                getMessage()?.warning('配合选中功能尚未接入装配体配合控制器');
                return;
            }

            selectedKeys.value = [key];
            /**
             * Mate 高亮必须由 cad-core 根据持久拓扑引用恢复参与实体。
             * 这里不调用普通 objectId 选中，避免把 mateId 当作场景 Object3D.uuid。
             */
            controller.selectMate(mateId);
            return;
        }
        clearAssemblyMateTreeSelectionHighlight();
        if (nodeMeta.objectType !== 'feature') {
            viewer?.setFeatureVisualSelectionScope?.(null);
        }
        selectedKeys.value = keys;
        ensureSketchRefVisibleForSelection(nodeMeta);
        if (previousDatumSlotDescriptor) {
            viewer?.clearImportedModelDatumSlotHighlight?.();
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            handleDatumPlaneClick(nodeMeta.planeId);
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            const axisSummary = viewer?.getDatumAxisSummary?.(nodeMeta.axisId) ?? null;
            selectedObject.value = axisSummary
                ? {
                    id: axisSummary.id,
                    type: 'datum-axis',
                    name: axisSummary.name,
                    metadata: {
                        type: 'Datum Axis',
                    },
                  }
                : null;
            //------ 设计树基准对象草图选中保持面板 开始------
            /**
             * 设计树选择基准轴时同步场景主选对象，但不触发右侧属性页切换。
             */
            const axisObjectId = viewer?.getDatumAxis?.(nodeMeta.axisId)?.getObject3D?.().uuid ?? null;
            if (axisObjectId) {
                viewer?.selectEntityByObjectId?.(axisObjectId, {
                    interactionMeta: createFeatureTreeDefaultSelectionMeta(axisSummary?.name),
                });
            }
            //------ 设计树基准对象草图选中保持面板 结束------
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            clearSelectedDatumPlanePreview();
            viewer?.stopSketchScaleControl?.();
            const pointSummary = viewer?.getDatumPointSummary?.(nodeMeta.pointId) ?? null;
            selectedObject.value = pointSummary
                ? {
                    id: pointSummary.id,
                    type: 'datum-point',
                    name: pointSummary.name,
                    metadata: {
                        type: 'Datum Point',
                    },
                  }
                : null;
            //------ 原点双向选择同步 开始------
            /**
             * 设计树选择基准点时，主动同步场景主选对象，
             * 保证“树选中 -> 场景选中”与导入模型链路保持一致。
             */
            const pointObjectId = pointSummary && typeof pointSummary.uuid === 'string'
                ? pointSummary.uuid
                : null;
            if (pointObjectId) {
                viewer?.selectEntityByObjectId?.(pointObjectId, {
                    interactionMeta: createFeatureTreeDefaultSelectionMeta(pointSummary?.name),
                });
            }
            //------ 原点双向选择同步 结束------
            return;
        }
        clearSelectedDatumPlanePreview();
        const objectId = nodeMeta.objectId || key;
        const interactionMeta = buildFeatureTreeSelectionInteractionMeta(nodeMeta);
        const selectionInteractionMeta = nodeMeta.kind === FeatureTreeNodeKind.Object && nodeMeta.objectType === 'sketch'
            ? createFeatureTreeDefaultSelectionMeta()
            : interactionMeta;
        const featureResultObjectIds = nodeMeta.objectType === 'feature' && Array.isArray(nodeMeta.resultObjectIds)
            ? nodeMeta.resultObjectIds.filter((resultObjectId) => resultObjectId.trim().length > 0)
            : [];
        const primarySelectionObjectId = featureResultObjectIds[0] ?? objectId;
        const sceneObject = getSceneObjectById(primarySelectionObjectId);
        //------ 活动草图节点主选隔离 开始------
        const isActiveSketchGroupSelection = isActiveSketchGroupTreeSelection({
            viewer,
            nodeMeta,
            primarySelectionObjectId,
            sceneObject,
        });
        //------ 活动草图节点主选隔离 结束------
        if (nodeMeta.objectType === 'feature' && !interactionMeta) {
            const featureVisualObjectIds = featureResultObjectIds.length > 0
                ? featureResultObjectIds
                : [primarySelectionObjectId];
            viewer?.setFeatureVisualSelectionScope?.(objectId, featureVisualObjectIds);
            viewer?.stopSketchScaleControl?.();
            selectedObject.value = await getObjectById(objectId);
            return;
        }
        //------ 设计树特征节点主选同步 开始------
        /**
         * 设计树选中特征或对象节点时，默认把对应场景对象同步为主选，
         * 不能只在 imported-model 这类带 interactionMeta 的节点下才触发。
         *
         * 说明：
         * 1. 普通 cad-core 特征节点通常没有 `interactionMeta`；
         * 2. 若这里不主动同步 `selectEntityByObjectId`，树选中只会停留在 web-client store，
         *    `InteractionStateManager` 无法收到主选变更；
         * 3. 后续“特征树选中 -> 对应特征面 Selected 高亮”依赖这条统一主选同步链；
         * 4. 当前正在编辑的草图组是例外：树节点只定位草图，不代表进入后默认全选。
         */
        if (!isActiveSketchGroupSelection) {
            if (featureResultObjectIds.length > 1) {
                viewer?.selectEntitiesByObjectIds?.(featureResultObjectIds, {
                    interactionMeta: selectionInteractionMeta ?? {
                        source: 'feature-tree',
                        semantic: 'entity',
                    },
                });
            } else if (selectionInteractionMeta) {
                viewer?.selectEntityByObjectId?.(
                    primarySelectionObjectId,
                    {interactionMeta: selectionInteractionMeta},
                );
            } else {
                viewer?.selectEntityByObjectId?.(primarySelectionObjectId);
            }
        }
        //------ 设计树特征节点主选同步 结束------
        if (syncImportedTreeSelectionSummary(primarySelectionObjectId, interactionMeta)) {
            viewer?.stopSketchScaleControl?.();
            return;
        }
        if (sceneObject && isSketchGroup(sceneObject)) {
            selectedObject.value = createSceneSelectionSummary(sceneObject, 'sketch');
            if (isActiveSketchGroupSelection) {
                viewer?.stopSketchScaleControl?.();
                return;
            }
            viewer?.startSketchScaleControl?.(sceneObject.uuid);
            return;
        }
        viewer?.stopSketchScaleControl?.();
        const sketchEntityType = sceneObject
            ? resolveSketchEntityTypeByMeshClass(sceneObject)
            : null;
        if (sceneObject && sketchEntityType) {
            selectedObject.value = createSceneSelectionSummary(sceneObject, sketchEntityType);
        } else {
            selectedObject.value = await getObjectById(objectId);
        }
        if (nodeMeta.objectType === 'body' && selectedObject.value) {
            const createdByFeatureId = nodeMeta.createdByFeatureId ?? objectId;
            selectedObject.value = {
                ...selectedObject.value,
                type: 'body',
                metadata: {
                    ...(selectedObject.value.metadata || {}),
                    type: 'Solid Body',
                    createdByFeatureId,
                },
            };
        }
    }

    /**
     * 处理设计树重命名。
     * @param key 节点键。
     * @param defaultName 设计树节点当前显示名。
     */
    async function handleFeatureRename(key: string, defaultName?: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const message = getMessage();
        const viewer = getViewer();
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group) {
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            const plane = viewer?.datumPlaneManager?.getPlane(nodeMeta.planeId);
            if (!plane) {
                return;
            }
            const newName = await requestRename('重命名基准面', plane.name);
            if (!newName) {
                return;
            }
            if (!viewer?.datumPlaneManager?.renamePlane(nodeMeta.planeId, newName)) {
                message?.warning('基准面重命名失败');
                return;
            }
            await refreshTree({key, name: plane.name, target: {kind: nodeMeta.kind, planeId: nodeMeta.planeId}});
            if (selectedObject.value?.id === plane.id) {
                selectedObject.value = {
                    ...selectedObject.value,
                    name: newName,
                    metadata: {
                        ...(selectedObject.value.metadata || {}),
                        name: newName,
                    },
                };
            }
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            const axis = viewer?.datumAxisManager?.getAxis(nodeMeta.axisId);
            if (!axis) {
                return;
            }
            const newName = await requestRename('重命名基准轴', axis.name);
            if (!newName) {
                return;
            }
            if (!viewer?.datumAxisManager?.renameAxis(nodeMeta.axisId, newName)) {
                message?.warning('基准轴重命名失败');
                return;
            }
            await refreshTree({key, name: axis.name, target: {kind: nodeMeta.kind, axisId: nodeMeta.axisId}});
            if (selectedObject.value?.id === axis.id) {
                selectedObject.value = {
                    ...selectedObject.value,
                    name: newName,
                    metadata: {
                        ...(selectedObject.value.metadata || {}),
                        name: newName,
                    },
                };
            }
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            const point = viewer?.datumPointManager?.getPoint(nodeMeta.pointId);
            if (!point) {
                return;
            }
            const newName = await requestRename('重命名基准点', point.name);
            if (!newName) {
                return;
            }
            if (!viewer?.datumPointManager?.renamePoint(nodeMeta.pointId, newName)) {
                message?.warning('基准点重命名失败');
                return;
            }
            await refreshTree({key, name: point.name, target: {kind: nodeMeta.kind, pointId: nodeMeta.pointId}});
            if (selectedObject.value?.id === point.id) {
                selectedObject.value = {
                    ...selectedObject.value,
                    name: newName,
                    metadata: {
                        ...(selectedObject.value.metadata || {}),
                        name: newName,
                    },
                };
            }
            return;
        }
        const objectId = nodeMeta.objectId || key;
        let currentName = typeof defaultName === 'string' ? defaultName.trim() : '';
        if (!currentName && nodeMeta.objectType === 'sketch') {
            currentName = viewer?.getSketchGroupName?.(objectId) ?? '';
        }
        if (!currentName && nodeMeta.objectType !== 'feature') {
            const sceneObject = getSceneObjectById(objectId);
            currentName = sceneObject?.name || '';
        }
        if (!currentName) {
            const currentObject = await getObjectById(objectId);
            const currentMetadataName = currentObject?.metadata?.name;
            currentName = typeof currentMetadataName === 'string'
                ? currentMetadataName
                : currentObject?.name || currentObject?.type || '';
        }
        const newName = await requestRename('重命名', currentName);
        if (!newName) {
            return;
        }
        /**
         * 特征节点的名称覆盖属于设计树投影语义，不能调用草图重命名接口；
         * 否则结果体 Object3D 与 Feature 命名会再次混在一起。
         */
        if (nodeMeta.objectType === 'feature') {
            objectNameOverrides.set(objectId, newName);
        } else if (!viewer?.renameSketchGroup?.(objectId, newName)) {
            objectNameOverrides.set(objectId, newName);
        }
        if (selectedObject.value?.id === objectId) {
            selectedObject.value = {
                ...selectedObject.value,
                name: newName,
                metadata: {
                    ...(selectedObject.value.metadata || {}),
                    name: newName,
                },
            };
        }
        await refreshTree({key, name: newName, target: {
            kind: nodeMeta.kind, objectId: nodeMeta.objectId, objectType: nodeMeta.objectType, featureKind: nodeMeta.featureKind,
        }});
    }

    /**
     * 处理编辑特征。
     * @param key 节点键。
     */
    async function handleFeatureEdit(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const onHandleFeatureEdit = getHandleFeatureEdit();
        //------ 基准面编辑特征 开始------
        if (nodeMeta?.kind === FeatureTreeNodeKind.DatumPlane) {
            const onHandleReferencePlaneEdit = getHandleReferencePlaneEdit();
            if (!nodeMeta.canEditReferencePlane || typeof nodeMeta.planeId !== 'number') {
                return;
            }
            if (!onHandleReferencePlaneEdit) {
                getMessage()?.warning('基准面编辑特征尚未接入');
                return;
            }
            await onHandleReferencePlaneEdit({
                planeId: nodeMeta.planeId,
                treeKey: key,
            });
            return;
        }
        //------ 基准面编辑特征 结束------
        //------ 基准轴编辑特征 开始------
        if (nodeMeta?.kind === FeatureTreeNodeKind.DatumAxis) {
            const onHandleReferenceAxisEdit = getHandleReferenceAxisEdit();
            if (!nodeMeta.canEditReferenceAxis || typeof nodeMeta.axisId !== 'number') {
                return;
            }
            if (!onHandleReferenceAxisEdit) {
                getMessage()?.warning('基准轴编辑特征尚未接入');
                return;
            }
            await onHandleReferenceAxisEdit({
                axisId: nodeMeta.axisId,
                treeKey: key,
            });
            return;
        }
        //------ 基准轴编辑特征 结束------
        //------ 基准点编辑特征 开始------
        if (nodeMeta?.kind === FeatureTreeNodeKind.DatumPoint) {
            const onHandleReferencePointEdit = getHandleReferencePointEdit();
            if (!nodeMeta.canEditReferencePoint || typeof nodeMeta.pointId !== 'number') {
                return;
            }
            if (!onHandleReferencePointEdit) {
                getMessage()?.warning('基准点编辑特征尚未接入');
                return;
            }
            await onHandleReferencePointEdit({
                pointId: nodeMeta.pointId,
                treeKey: key,
            });
            return;
        }
        //------ 基准点编辑特征 结束------
        //------ 线性零部件阵列编辑特征 开始------
        if (
            nodeMeta?.kind === FeatureTreeNodeKind.GlbNode
            && nodeMeta.objectType === LinearComponentPatternRuntimeNodeType.Group
        ) {
            const onEditLinearComponentPattern = getHandleLinearComponentPatternEdit();
            const groupObjectId = (nodeMeta.glbSourceObjectId ?? nodeMeta.objectId ?? '').trim();
            if (!onEditLinearComponentPattern) {
                getMessage()?.warning('线性阵列编辑功能尚未就绪');
                return;
            }
            if (!groupObjectId) {
                getMessage()?.warning('线性阵列节点对象标识缺失，无法编辑');
                return;
            }
            await onEditLinearComponentPattern({
                groupObjectId,
                treeKey: key,
            });
            return;
        }
        //------ 线性零部件阵列编辑特征 结束------
        if (!nodeMeta || !nodeMeta.objectId || !nodeMeta.featureKind || !nodeMeta.canEditFeature) {
            return;
        }
        if (!onHandleFeatureEdit) {
            return;
        }
        await onHandleFeatureEdit({
            featureId: nodeMeta.objectId,
            featureKind: nodeMeta.featureKind,
            sourceSketchId: nodeMeta.sourceSketchId,
        });
        /**
         * 编辑特征会进入 rollback sandbox，刷新后设计树才能把目标特征及后续节点同步为禁用态。
         */
        await refreshTree();
    }

    /**
     * 处理编辑草图。
     *
     * 设计说明：
     * 1. actions 层负责把 sketch 节点和 feature 节点统一解析为目标 `sketchGroupId`；
     * 2. provider 端只恢复草图编辑会话，不再根据入口来源维护两套状态链路；
     * 3. 若特征声明可编辑草图但缺少 sourceSketchId，说明投影数据不完整，需要明确提示。
     *
     * @param key 节点键。
     */
    async function handleFeatureEditSketch(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const onHandleFeatureEditSketch = getHandleFeatureEditSketch();
        const resolvedRequest = resolveEditSketchRequest({
            key,
            nodeMeta,
            featureTreeNodeMap,
        });
        if (resolvedRequest.status === 'missing-source-sketch') {
            getMessage()?.warning('该特征没有来源草图');
            return;
        }
        if (resolvedRequest.status === 'missing-sketch-node') {
            getMessage()?.warning('未找到来源草图节点');
            return;
        }
        if (resolvedRequest.status !== 'resolved' || !resolvedRequest.request) {
            return;
        }
        if (!resolvedRequest.selectionKey) {
            getMessage()?.warning('未找到来源草图节点');
            return;
        }
        if (!onHandleFeatureEditSketch) {
            getMessage()?.warning('编辑草图功能尚未接入');
            return;
        }
        useDispatchEvent('toolbarSwitchTab', 'sketch');
        const editResult = await onHandleFeatureEditSketch(resolvedRequest.request);
        if (editResult === false) {
            return;
        }
        /**
         * 编辑特征下草图会让 cad-core 将回滚游标移动到目标特征之前。
         * 这里必须在选中草图节点前刷新投影，让设计树复用 rollbackState 把目标特征及后续节点置灰。
         */
        await refreshTree();
        /**
         * 必须在底层草图会话恢复成功之后再执行树选中。
         * 原因：恢复草图会清理旧命令态和临时选择，若提前选中草图节点，清理事件会反向清空
         * 设计树选中态，最终造成草图不显示、工具栏不高亮、端点拖动不走草图选中重建链路。
         */
        await handleFeatureSelect([resolvedRequest.selectionKey]);
    }

    /**
     * 处理删除特征。
     */
    /**
     * 通过 Editor 历史边界执行草图生命周期动作。
     *
     * 设计说明：
     * 设计树不能直接调用 Viewer 删除/隐藏/显示草图，否则这些模型变化不会进入 undo/redo。
     * 这里仅分发语义动作，实际 before/after 快照由 Editor 统一捕获并提交模型历史。
     *
     * @param action 动作类型。
     * @param key 设计树节点 key。
     * @param sketchGroupId 草图组 ID。
     * @param failureMessage 失败提示。
     * @returns 动作是否成功。
     */
    async function runSketchLifecycleAction(
        action: 'delete' | 'hide' | 'show' | 'copy',
        key: string,
        sketchGroupId: string,
        failureMessage: string,
    ): Promise<boolean> {
        const actionHandler = getHandleSketchLifecycleAction();
        const message = getMessage();

        if (!actionHandler) {
            message?.warning('草图设计树操作尚未接入历史边界');
            return false;
        }

        const succeeded = await actionHandler({
            action,
            sketchGroupId,
            treeKey: key,
        });

        if (!succeeded) {
            message?.warning(failureMessage);
            return false;
        }

        if (action === 'hide' || action === 'show') {
            await refreshNodeVisibility(key, action === 'hide');
        } else {
            await refreshTree();
        }
        return true;
    }

    /**
     * 优先增量更新已存在节点的可见性，无法稳定定位时回退完整投影。
     *
     * @param key 设计树稳定节点 key。
     * @param hidden 节点是否隐藏。
     */
    async function refreshNodeVisibility(key: string, hidden: boolean): Promise<void> {
        if (applyNodeVisibilityChange(key, hidden)) {
            return;
        }
        await refreshTree();
    }

    //------ 基准对象删除历史 开始------
    /**
     * 通过 Editor 历史边界执行基准对象删除。
     *
     * 设计说明：
     * 设计树中的基准面、基准轴、基准点都属于 CAD 主状态，删除时必须由 Editor 捕获
     * before/after 快照并提交模型历史。动作层只分发基准对象类型与稳定 ID，避免继续直写 manager。
     *
     * @param input 基准对象删除动作。
     * @param failureMessage 失败提示。
     * @returns 动作是否成功。
     */
    async function runDatumReferenceDeleteAction(
        input: FeatureTreeDatumReferenceDeleteActionInput,
        failureMessage: string,
    ): Promise<boolean> {
        const actionHandler = getHandleDatumReferenceDeleteAction();
        const message = getMessage();

        if (!actionHandler) {
            message?.warning('基准对象删除操作尚未接入历史边界');
            return false;
        }

        const succeeded = await actionHandler(input);

        if (!succeeded) {
            message?.warning(failureMessage);
            return false;
        }

        await refreshTree();
        return true;
    }
    //------ 基准对象删除历史 结束------

    /**
     * 通过 Editor 历史边界执行特征显隐动作。
     *
     * 设计说明：
     * 特征显隐会改变 CAD 模型显示状态，必须像草图显隐一样进入模型历史边界；
     * action 层只分发 featureId 与动作语义，不直接操作 Viewer 结果体。
     *
     * @param action 动作类型。
     * @param key 设计树节点 key。
     * @param featureId 特征 ID。
     * @param failureMessage 失败提示。
     * @returns 动作是否成功。
     */
    async function runFeatureVisibilityAction(
        action: 'hide' | 'show',
        key: string,
        featureId: string,
        failureMessage: string,
    ): Promise<boolean> {
        const actionHandler = getHandleFeatureVisibilityAction();
        const message = getMessage();

        if (!actionHandler) {
            message?.warning('特征显隐操作尚未接入历史边界');
            return false;
        }

        const succeeded = await actionHandler({
            action,
            featureId,
            treeKey: key,
        });

        if (!succeeded) {
            message?.warning(failureMessage);
            return false;
        }

        await refreshNodeVisibility(key, action === 'hide');
        return true;
    }

    async function handleFeatureDelete(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const message = getMessage();
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group) {
            return;
        }
        if (isAssemblyMateTreeNode(nodeMeta)) {
            const mateId = resolveAssemblyMateId(nodeMeta);
            const controller = getAssemblyMateController();
            if (!mateId) {
                message?.warning('Mate 节点缺少可删除的配合 ID');
                return;
            }
            if (!controller?.deleteMate) {
                message?.warning('配合删除功能尚未接入装配体配合控制器');
                return;
            }

            /**
             * 删除 Mate 属于 cad-core 文档命令。删除后清除 Mate 高亮并立即刷新树，
             * 避免设计树继续显示已删除的配合关系。
             */
            const dialog = getDialog();
            if (!dialog) {
                message?.warning('配合删除确认弹窗尚未接入');
                return;
            }
            if (!await requestFeatureTreeDeleteConfirmation({
                dialog,
                target: {
                    id: mateId,
                    label: resolveAssemblyMateDeleteLabel(nodeMeta, mateId),
                    kind: 'mate',
                },
                relatedItems: [],
            })) {
                return;
            }
            await controller.deleteMate(mateId);
            controller.selectMate?.(null);
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            await refreshTree();
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            const succeeded = await runDatumReferenceDeleteAction({
                kind: FeatureTreeDatumReferenceKind.Plane,
                datumId: nodeMeta.planeId,
                treeKey: key,
            }, '基准面删除失败');
            if (!succeeded) {
                return;
            }
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
                clearSelectedDatumPlanePreview();
            }
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            const succeeded = await runDatumReferenceDeleteAction({
                kind: FeatureTreeDatumReferenceKind.Axis,
                datumId: nodeMeta.axisId,
                treeKey: key,
            }, '基准轴删除失败');
            if (!succeeded) {
                return;
            }
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            const succeeded = await runDatumReferenceDeleteAction({
                kind: FeatureTreeDatumReferenceKind.Point,
                datumId: nodeMeta.pointId,
                treeKey: key,
            }, '基准点删除失败');
            if (!succeeded) {
                return;
            }
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            return;
        }
        if (nodeMeta.objectType === 'sketch' && nodeMeta.objectId) {
            if (nodeMeta.nodeRole === 'sketchRef') {
                message?.warning('被特征吸收的草图请通过编辑特征或来源草图链路处理，当前不支持直接删除引用节点');
                return;
            }
            if (!await runSketchLifecycleAction('delete', key, nodeMeta.objectId, '草图删除失败')) {
                return;
            }
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            return;
        }
        if (nodeMeta.objectType === 'feature' && nodeMeta.objectId) {
            const onDeleteFeature = getHandleFeatureDeleteAction();

            if (!onDeleteFeature) {
                message?.warning('特征删除功能尚未接入历史边界');
                return;
            }

            const succeeded = await onDeleteFeature({
                featureId: nodeMeta.objectId,
                treeKey: key,
                objectNameOverrides,
            });

            if (!succeeded) {
                return;
            }

            await refreshTree();
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            return;
        }
        if (isAssemblyComponentDeleteTarget(nodeMeta)) {
            const onDeleteAssemblyComponent = getHandleAssemblyComponentDeleteAction();
            if (!onDeleteAssemblyComponent) {
                message?.warning('装配组件删除功能尚未就绪');
                return;
            }

            const deleteInput = createAssemblyComponentDeleteActionInput(key, nodeMeta);
            if (!deleteInput) {
                message?.warning('装配组件节点对象标识缺失，无法删除');
                return;
            }

            const succeeded = await onDeleteAssemblyComponent(deleteInput);
            if (!succeeded) {
                return;
            }
            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            return;
        }
        //------ 线性零部件阵列节点删除 开始------
        if (
            nodeMeta.kind === FeatureTreeNodeKind.GlbNode
            && nodeMeta.objectType === LinearComponentPatternRuntimeNodeType.Group
        ) {
            const onDeleteLinearComponentPattern = getHandleLinearComponentPatternDelete();
            if (!onDeleteLinearComponentPattern) {
                message?.warning('线性阵列删除功能尚未就绪');
                return;
            }

            const groupObjectId = (nodeMeta.glbSourceObjectId ?? nodeMeta.objectId ?? '').trim();
            if (!groupObjectId) {
                message?.warning('线性阵列节点对象标识缺失，无法删除');
                return;
            }

            await onDeleteLinearComponentPattern({
                groupObjectId,
                treeKey: key,
            });

            if (selectedKeys.value.includes(key)) {
                selectedKeys.value = [];
                selectedObject.value = null;
            }
            return;
        }
        //------ 线性零部件阵列节点删除 结束------
        message?.warning('当前节点暂未接入删除能力');
    }

    /**
     * 处理隐藏特征。
     */
    async function handleFeatureHide(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const viewer = getViewer();
        const message = getMessage();
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group) {
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            viewer?.datumPlaneManager?.setPlaneVisible(nodeMeta.planeId, false);
            clearSelectedDatumPlanePreview();
            await refreshNodeVisibility(key, true);
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            viewer?.datumAxisManager?.setAxisVisible(nodeMeta.axisId, false);
            await refreshNodeVisibility(key, true);
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            viewer?.datumPointManager?.setPointVisible(nodeMeta.pointId, false);
            await refreshNodeVisibility(key, true);
            return;
        }
        if (nodeMeta.objectType === 'sketch' && nodeMeta.objectId) {
            if (!await runSketchLifecycleAction('hide', key, nodeMeta.objectId, '草图隐藏失败')) {
                return;
            }
            return;
        }
        if (nodeMeta.objectType === 'feature' && nodeMeta.objectId) {
            if (!await runFeatureVisibilityAction('hide', key, nodeMeta.objectId, '特征隐藏失败')) {
                return;
            }
            return;
        }
        message?.warning('当前节点暂未接入隐藏能力');
    }

    /**
     * 处理复制草图。
     */
    async function handleFeatureCopySketch(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const message = getMessage();
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group || nodeMeta.objectType !== 'sketch' || !nodeMeta.objectId) {
            message?.warning('当前节点不支持复制草图');
            return;
        }
        await runSketchLifecycleAction('copy', key, nodeMeta.objectId, '草图复制失败');
    }

    /**
     * 处理显示特征。
     */
    async function handleFeatureShow(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const viewer = getViewer();
        const message = getMessage();
        if (!nodeMeta || nodeMeta.kind === FeatureTreeNodeKind.Group) {
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPlane && hasFeatureTreeDatumPlaneId(nodeMeta.planeId)) {
            viewer?.datumPlaneManager?.setPlaneVisible(nodeMeta.planeId, true);
            await refreshNodeVisibility(key, false);
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumAxis && nodeMeta.axisId) {
            viewer?.datumAxisManager?.setAxisVisible(nodeMeta.axisId, true);
            await refreshNodeVisibility(key, false);
            return;
        }
        if (nodeMeta.kind === FeatureTreeNodeKind.DatumPoint && nodeMeta.pointId) {
            viewer?.datumPointManager?.setPointVisible(nodeMeta.pointId, true);
            await refreshNodeVisibility(key, false);
            return;
        }
        if (nodeMeta.objectType === 'sketch' && nodeMeta.objectId) {
            if (!await runSketchLifecycleAction('show', key, nodeMeta.objectId, '草图显示失败')) {
                return;
            }
            return;
        }
        if (nodeMeta.objectType === 'feature' && nodeMeta.objectId) {
            if (!await runFeatureVisibilityAction('show', key, nodeMeta.objectId, '特征显示失败')) {
                return;
            }
            return;
        }
        message?.warning('当前节点暂未接入显示能力');
    }

    /**
     * 处理装配体零部件固定/浮动切换。
     *
     * 设计说明：
     * 1. 设计树动作层只校验节点语义与归一化对象 ID；
     * 2. 真正 hierarchy 与场景对象同步由 provider 收口，避免 UI 层直接写底层状态；
     * 3. sceneObjectId 保留为运行时镜像写入锚点，objectId 保留为 hierarchy 权威更新锚点。
     *
     * @param key 设计树节点 key。
     * @param state 目标固定/浮动状态。
     */
    async function handleAssemblyComponentFixationChange(
        key: string,
        state: AssemblyComponentFixationState,
    ): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const message = getMessage();
        const onChange = getHandleAssemblyComponentFixationChange();
        if (
            !nodeMeta
            || nodeMeta.kind !== FeatureTreeNodeKind.GlbNode
            || !nodeMeta.canFixOrFloat
            || !nodeMeta.objectId
        ) {
            message?.warning('当前节点不支持固定/浮动');
            return;
        }
        if (!onChange) {
            message?.warning('固定/浮动功能尚未就绪');
            return;
        }

        await onChange({
            objectId: nodeMeta.glbSourceObjectId ?? nodeMeta.objectId,
            sceneObjectId: nodeMeta.objectId,
            state,
            treeKey: key,
        });
    }

    /**
     * 处理设计树 Mate 抑制状态切换。
     *
     * 设计说明：
     * 1. 抑制 Mate 会改变装配体文档主状态，必须通过 cad-core `SuppressAssemblyMateCommand`；
     * 2. web-client 只读取树节点中的 `mateId`，不修改 Mate Definition；
     * 3. 完成后刷新设计树，让状态图标和右键菜单从最新文档重新投影。
     *
     * @param key 设计树节点 key。
     * @param suppressed 目标抑制状态。
     */
    async function handleAssemblyMateSuppressChange(
        key: string,
        suppressed: boolean,
    ): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const mateId = resolveAssemblyMateId(nodeMeta);
        const controller = getAssemblyMateController();
        if (!isAssemblyMateTreeNode(nodeMeta) || !mateId) {
            getMessage()?.warning('当前节点不支持配合抑制');
            return;
        }
        if (!controller?.suppressMate) {
            getMessage()?.warning('配合抑制功能尚未接入装配体配合控制器');
            return;
        }

        await controller.suppressMate(mateId, suppressed);
        if (selectedKeys.value.includes(key)) {
            controller.selectMate?.(mateId);
        }
        await refreshTree();
    }

    /**
     * 处理设计树配合文件夹的“锁定同轴心旋转”动作。
     *
     * 设计说明：
     * 1. SOLIDWORKS 在 Mates 文件夹右键提供该动作；
     * 2. web-client 只负责触发 cad-core controller，不判断哪些 Mate 可锁定；
     * 3. cad-core 返回空列表时表示没有产生文档变更，不刷新历史。
     *
     * @param key 设计树配合文件夹节点 key。
     */
    async function handleAssemblyMateFolderLockConcentricRotation(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const controller = getAssemblyMateController();
        const message = getMessage();
        if (nodeMeta?.kind !== FeatureTreeNodeKind.AssemblyMateFolder) {
            message?.warning('当前节点不支持锁定同轴心旋转');
            return;
        }
        if (!controller?.lockConcentricRotationForAllMates) {
            message?.warning('锁定同轴心旋转功能尚未接入装配体配合控制器');
            return;
        }

        const affectedMateIds = await controller.lockConcentricRotationForAllMates({
            ownerAssemblyNodeId: nodeMeta.assemblyMateOwnerAssemblyNodeId ?? null,
            ownerComponentNodeId: nodeMeta.assemblyMateOwnerComponentNodeId ?? null,
        });
        if (affectedMateIds.length === 0) {
            message?.info('当前配合文件夹没有可锁定旋转的同轴心配合');
            return;
        }
        await refreshTree();
    }

    /**
     * 处理设计树 Mate 编辑入口。
     *
     * 设计说明：
     * 1. 设计树动作层只把树节点解析为稳定 `mateId`；
     * 2. 既有 Mate 的 PropertyManager 状态恢复由 cad-core 控制器完成；
     * 3. 右侧面板与工具栏命令态由 Editor 工作流 provider 编排，避免动作层依赖具体 UI 宿主。
     *
     * @param key 设计树节点 key。
     */
    async function handleAssemblyMateEdit(key: string): Promise<void> {
        const nodeMeta = featureTreeNodeMap.get(key);
        const mateId = resolveAssemblyMateId(nodeMeta);
        const handler = getHandleAssemblyMateEdit();
        if (!isAssemblyMateTreeNode(nodeMeta) || !mateId) {
            getMessage()?.warning('当前节点不支持编辑配合');
            return;
        }
        if (!handler) {
            getMessage()?.warning('配合编辑功能尚未接入装配体配合工作流');
            return;
        }

        const result = await handler({
            mateId,
            treeKey: key,
        });
        if (result === false) {
            return;
        }
        selectedKeys.value = [key];
        selectedObject.value = null;
    }

    /**
     * 处理创建基准面。
     */
    function handleCreateDatumPlane(): void {
    }

    return {
        handleDatumPlaneClick,
        handleDatumPlaneTreeSelect,
        handleDatumPlaneHover,
        handleDatumReferenceHover,
        handleFeatureNodeHover,
        handleAssemblyMateNodeHover,
        handleFeatureNodeHoverTargets,
        handleEnterDatumPlaneSketch,
        //------ 设计树正视于 开始------
        handleFeatureTreeNormalTo,
        //------ 设计树正视于 结束------
        handleFeatureSelect,
        handleFeatureNodeSelectTargets,
        handleFeatureRename,
        handleFeatureEdit,
        handleFeatureEditSketch,
        handleFeatureRollbackToFeature,
        handleFeatureRollbackForward,
        handleFeatureRollbackBarMoveToIndex,
        handleFeatureRollbackBarMoveUp,
        handleFeatureRollbackBarMoveDown,
        handleFeatureRollbackToPrevious,
        handleFeatureRollbackToEnd,
        handleFeatureDelete,
        handleFeatureHide,
        handleFeatureCopySketch,
        handleFeatureShow,
        handleAssemblyComponentFixationChange,
        handleAssemblyMateSuppressChange,
        handleAssemblyMateFolderLockConcentricRotation,
        handleAssemblyMateEdit,
        handleCreateDatumPlane,
    };
}
